import DjangoMessages from 'components/DjangoMessages';
import LinkButton from 'components/LinkButton';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import SectionHeader from 'components/SectionHeader';
import TablePagination from 'components/Table/TableComponents/TablePagination';
import TableStatic from 'components/Table/TableStatic';
import TooltipWrapper from 'components/Tooltip';
import usePaginatedData from 'hooks/usePaginatedData';
import {useBackend} from 'state/backend';
import {t} from 'ttag';

import {GroupsAndRolesBackend} from './types';

const GroupsSection = () => {
    const {
        data: {groups, permissions},
    } = useBackend<GroupsAndRolesBackend>();

    const {paginatedData, ...pagination} = usePaginatedData(groups, 10);

    return (
        <div className="space-y-4">
            <SectionHeader
                subtitle={t`Documents and clients owned by a group are only visible to members of the group`}
                title={t`Groups`}
            />
            <TableStatic id="groups-table">
                <TableStatic.Header>
                    <TableStatic.HeaderRow>
                        <TableStatic.Column>{t`Group name`}</TableStatic.Column>
                        <TableStatic.Column>
                            {t`Member count`}
                        </TableStatic.Column>
                        <TableStatic.Column>
                            {t`Client count`}
                        </TableStatic.Column>
                        <TableStatic.Column />
                    </TableStatic.HeaderRow>
                </TableStatic.Header>
                <TableStatic.Body>
                    {paginatedData.map((group) =>
                        group ? (
                            <TableStatic.Row key={group.mid}>
                                <TableStatic.Cell>
                                    <a
                                        href={window.Urls.team_item_view(
                                            group.mid,
                                        )}
                                    >
                                        {group.name}
                                    </a>
                                </TableStatic.Cell>
                                <TableStatic.Cell>
                                    {group.memberCount || ''}
                                </TableStatic.Cell>
                                <TableStatic.Cell>
                                    {group.clientCount || ''}
                                </TableStatic.Cell>
                                <TableStatic.Cell className="float-right">
                                    <TooltipWrapper
                                        message={
                                            permissions.canEditTeam
                                                .failureReason
                                        }
                                    >
                                        <LinkButton
                                            disabled={
                                                !permissions.canEditTeam.allowed
                                            }
                                            href={window.Urls.team_edit_view(
                                                group.mid,
                                            )}
                                            kind="secondary"
                                            size="xs"
                                        >
                                            {t`Edit`}
                                        </LinkButton>
                                    </TooltipWrapper>
                                </TableStatic.Cell>
                            </TableStatic.Row>
                        ) : (
                            <TableStatic.Row
                                className="bg-grey-100"
                                key={Math.random()}
                            >
                                <TableStatic.Cell>&nbsp;</TableStatic.Cell>
                                <TableStatic.Cell>&nbsp;</TableStatic.Cell>
                                <TableStatic.Cell>&nbsp;</TableStatic.Cell>
                                <TableStatic.Cell>&nbsp;</TableStatic.Cell>
                            </TableStatic.Row>
                        ),
                    )}
                    {groups.length === 0 && (
                        <TableStatic.Row>
                            <TableStatic.Cell colSpan={4}>
                                {t`You haven't created any groups yet. Create a group to control visibility of documents and clients.`}
                            </TableStatic.Cell>
                        </TableStatic.Row>
                    )}
                </TableStatic.Body>
            </TableStatic>
            <TablePagination
                id="groups-table-pagination"
                {...pagination}
                hideForSinglePage={true}
            />
            <div className="flex">
                <TooltipWrapper message={permissions.canEditTeam.failureReason}>
                    <LinkButton
                        disabled={!permissions.canEditTeam.allowed}
                        href={window.Urls.team_create_view('group')}
                        kind="secondary"
                    >
                        {t`Create Group`}
                    </LinkButton>
                </TooltipWrapper>
            </div>
        </div>
    );
};

const RolesSection = () => {
    const {
        data: {permissions, roles},
    } = useBackend<GroupsAndRolesBackend>();

    const {paginatedData, ...pagination} = usePaginatedData(roles, 10);

    return (
        <div className="space-y-4">
            <SectionHeader
                subtitle={t`Assign team members to different functional roles within your company`}
                title={t`Roles`}
            />
            <TableStatic id="roles-table">
                <TableStatic.Header>
                    <TableStatic.HeaderRow>
                        <TableStatic.Column>{t`Role Name`}</TableStatic.Column>
                        <TableStatic.Column>
                            {t`Member count`}
                        </TableStatic.Column>
                        <TableStatic.Column />
                    </TableStatic.HeaderRow>
                </TableStatic.Header>
                <TableStatic.Body>
                    {paginatedData.map((role) =>
                        role ? (
                            <TableStatic.Row key={role.mid}>
                                <TableStatic.Cell>
                                    <a
                                        href={window.Urls.team_item_view(
                                            role.mid,
                                        )}
                                    >
                                        {role.name}
                                    </a>
                                </TableStatic.Cell>
                                <TableStatic.Cell>
                                    {role.memberCount || ''}
                                </TableStatic.Cell>
                                <TableStatic.Cell className="float-right">
                                    <TooltipWrapper
                                        message={
                                            permissions.canEditTeam
                                                .failureReason
                                        }
                                    >
                                        <LinkButton
                                            disabled={
                                                !permissions.canEditTeam.allowed
                                            }
                                            href={window.Urls.team_edit_view(
                                                role.mid,
                                            )}
                                            kind="secondary"
                                            size="xs"
                                        >
                                            {t`Edit`}
                                        </LinkButton>
                                    </TooltipWrapper>
                                </TableStatic.Cell>
                            </TableStatic.Row>
                        ) : (
                            <TableStatic.Row
                                className="bg-grey-100"
                                key={Math.random()}
                            >
                                <TableStatic.Cell>&nbsp;</TableStatic.Cell>
                                <TableStatic.Cell>&nbsp;</TableStatic.Cell>
                                <TableStatic.Cell>&nbsp;</TableStatic.Cell>
                            </TableStatic.Row>
                        ),
                    )}
                    {roles.length === 0 && (
                        <TableStatic.Row>
                            <TableStatic.Cell colSpan={3}>
                                {t`You haven't created any roles yet. Create roles to replicate your company's functional structure.`}
                            </TableStatic.Cell>
                        </TableStatic.Row>
                    )}
                </TableStatic.Body>
            </TableStatic>
            <TablePagination
                id="roles-table-pagination"
                {...pagination}
                hideForSinglePage={true}
            />
            <div className="flex">
                <TooltipWrapper message={permissions.canEditTeam.failureReason}>
                    <LinkButton
                        disabled={!permissions.canEditTeam.allowed}
                        href={window.Urls.team_create_view('role')}
                        kind="secondary"
                    >
                        {t`Create Role`}
                    </LinkButton>
                </TooltipWrapper>
            </div>
        </div>
    );
};

const GroupsAndRoles = () => {
    const {
        data: {navigationTabs},
    } = useBackend<GroupsAndRolesBackend>();

    return (
        <div className="space-y-8">
            <DjangoMessages />
            <NavigationTabs.Team
                selection="groupsAndRoles"
                {...navigationTabs}
            />
            <GroupsSection />
            <RolesSection />
        </div>
    );
};

export default GroupsAndRoles;
